import { useQuery } from '@tanstack/react-query';
import { isEmpty, isPlainObject, isUndefined, omitBy, isEqual } from 'lodash';
import queryString from 'query-string';

import { QueryStringParams } from '@xtnd/web/shared';
import { useQueryKeyStore } from '@xtnd/web/shared/stores';

import { reportingEmailQueueApi } from '../api';
import { ArTemplateResponse, EmailQueueResponse } from '../types';
import { EMAIL_SORT_BY } from '../types';
import { ArEmailGroupByOption } from '@repo/types';
import { getAgingBucketBounds } from '../components/email/utils/agingBuckets';

export const useEmailQueue = (params: QueryStringParams, options?: { enabled?: boolean }) => {
  // agingBucket is FE/UI-only state; the wire contract is scalar bounds
  // (daysPastDue, daysPastDueCap). Translate at the boundary so the BE filter
  // primitive is uniform across list and matter-detail endpoints.
  const { agingBucket, ...wireFilters } = (params.filters ?? {}) as Record<string, any>;
  const groupBy = wireFilters.groupBy;
  const bucketBounds =
    groupBy === ArEmailGroupByOption.NONE && agingBucket ? getAgingBucketBounds(agingBucket) : {};

  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 50,
    filters: {
      ...wireFilters,
      ...(bucketBounds.daysPastDue !== undefined && { daysPastDue: bucketBounds.daysPastDue }),
      ...(bucketBounds.daysPastDueCap !== undefined && {
        daysPastDueCap: bucketBounds.daysPastDueCap,
      }),
      search: wireFilters.search,
      groupBy: wireFilters.groupBy,
    },
  };

  const sorts = params.sorts as Record<EMAIL_SORT_BY, 'asc' | 'desc'>;

  // sorting by 1 field
  if (sorts) {
    const values = (Object.entries(sorts) as [EMAIL_SORT_BY, 'asc' | 'desc' | undefined][]).find(
      ([, order]) => order !== undefined
    );

    if (values) {
      const [sortBy, order] = values;
      queryParams.sortBy = sortBy;
      queryParams.order = order;
    }
  }

  const cleanedParams = omitBy(
    queryParams,
    value => isUndefined(value) || (isPlainObject(value) && isEmpty(value))
  );

  const query = queryString.stringify(
    {
      ...cleanedParams,
      filters: cleanedParams.filters ? JSON.stringify(cleanedParams.filters) : undefined,
    },
    { arrayFormat: 'comma' }
  );

  const queryKey = ['email-queue', query];

  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  const emailQueueQueryKey = getQueryKey('ar-collections', 'reporting.email-queue');
  if (!isEqual(emailQueueQueryKey, queryKey)) {
    setQueryKey('ar-collections', 'reporting.email-queue', queryKey);
  }

  const queryResult = useQuery<EmailQueueResponse, Error>({
    queryKey,
    queryFn: () => reportingEmailQueueApi.fetchEmailQueue(query),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    ...queryResult,
    queryKey,
  };
};

export const useEmailQueueSubItems = (entityId: string | null, groupBy: ArEmailGroupByOption) => {
  const query = queryString.stringify(
    { filters: JSON.stringify({ groupBy, entityId }) },
    { arrayFormat: 'comma' }
  );
  const queryKey = ['email-queue-sub-items', groupBy, entityId];
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  const subItemsQueryKey = getQueryKey('ar-collections', 'reporting.email-queue-sub-items');
  if (!isEqual(subItemsQueryKey, queryKey)) {
    setQueryKey('ar-collections', 'reporting.email-queue-sub-items', queryKey);
  }
  const queryResult = useQuery<EmailQueueResponse, Error>({
    queryKey: ['email-queue-sub-items', groupBy, entityId],
    queryFn: () => reportingEmailQueueApi.fetchEmailQueue(query),
    enabled: !!entityId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });

  return {
    ...queryResult,
  };
};

export const useArTemplate = (
  matterId: string,
  clientId: string,
  options?: {
    enabled?: boolean;
    daysPastDue?: number | null;
    daysPastDueCap?: number | null;
  }
) => {
  const daysPastDue = options?.daysPastDue ?? null;
  const daysPastDueCap = options?.daysPastDueCap ?? null;
  const queryKey = ['email-queue-template', matterId, clientId, daysPastDue, daysPastDueCap];
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  const emailTemplateQueryKey = getQueryKey('ar-collections', 'reporting.email-queue-template');
  if (!isEqual(emailTemplateQueryKey, queryKey)) {
    setQueryKey('ar-collections', 'reporting.email-queue-template', queryKey);
  }

  return useQuery<ArTemplateResponse, Error>({
    queryKey,
    queryFn: () =>
      reportingEmailQueueApi.fetchTemplate(matterId, clientId, daysPastDue, daysPastDueCap),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
